import styled, { css, keyframes } from 'styled-components';

/* A living gradient, but NOT the Menu AI glow's 0-to-200% drift: that one
   crosses the background tile's edge, where the ramp jumps from magenta back
   to blue, and on a surface this wide the jump reads as a hard vertical seam
   sweeping through the card. Gliding 0-to-100% and back keeps the window
   inside one tile, so every frame is a smooth slice of the ramp. */
const flow = keyframes`
  from { background-position: 0% 50%; }
  to { background-position: 100% 50%; }
`;

/**
 * The AI gradient as a fill. Shared by the selected card and the count
 * bubble, so the two can never drift to different ramps.
 */
const gradientFill = css`
  background: ${({ theme }) => theme.aiGradient};
  background-size: 200% 100%;
`;

/** Hands the card the list's own 12px gutter, above the "Messages" title. */
export const ViktorEntrySlot = styled.div`
  flex: none;
  padding: 12px 12px 0;
`;

/**
 * The inbox rows' own states, with the gradient in the brand red's place.
 *
 * At rest it is a row like the ones under it - transparent, grey on hover -
 * and the gradient is worn by the disc and the name alone, which is what
 * makes it read as Viktor's row rather than as a banner over the list. Open,
 * it fills with the gradient the way a selected row fills with red, and
 * everything inside inverts to white to match.
 */
export const ViktorEntryButton = styled.button<{ $selected: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border: none;
  /* The inbox rows' radius, so the card reads as their pinned sibling. */
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  background: transparent;
  transition: ${({ theme }) => theme.transitions.ease200ms};

  :hover:not(:disabled) {
    background: ${({ theme }) => theme.gray};
  }

  ${({ $selected }) =>
    $selected &&
    css`
      ${gradientFill}
      animation: ${flow} 4s ease-in-out infinite alternate;

      :hover:not(:disabled) {
        ${gradientFill}
      }
    `}

  /* Only while the thread's id is still on its way - a click has nowhere to
     go yet. Kept visible rather than hidden so the column does not jump. */
  :disabled {
    cursor: progress;
  }
`;

export const ViktorEntryText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

/** One of the card's two lines - the inbox rows' name/preview geometry. */
export const ViktorEntryLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`;

/** The half of a line that gives way when space runs out. */
export const ViktorEntryShrink = styled.div`
  flex: 1;
  min-width: 0;
`;

/**
 * The name, in the rows' 16/22 bold - written in the gradient at rest, the
 * way the disc beside it is, and in white once the card itself is the
 * gradient. Clipped to the glyphs rather than coloured: a gradient has no
 * single value to hand `color`, and the clip is what lets the ramp run
 * across the letters.
 *
 * Inline-block, and the gradient at its natural size: the ramp is laid over
 * the element's box, so on a block spanning the whole line - or with the
 * card's 200% tile - the two words only ever covered the blue end of it and
 * read as plain blue. Sized to the text, the whole blue-to-magenta ramp
 * lands inside the letters.
 */
export const ViktorEntryName = styled.span<{ $selected: boolean }>`
  display: inline-block;
  max-width: 100%;
  vertical-align: top;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ $selected, theme }) =>
    $selected
      ? css`
          color: ${theme.white};
        `
      : css`
          background: ${theme.aiGradient};
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          -webkit-text-fill-color: transparent;
        `}
`;

/**
 * Answers waiting to be read: the inbox rows' count bubble with the gradient
 * in place of the red - and, on the open card, turned inside out the way a
 * selected row's is, since a gradient disc on the gradient would be nothing.
 */
export const ViktorEntryCount = styled.span<{ $selected: boolean }>`
  flex: none;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  line-height: 1;
  font-weight: 600;

  ${({ $selected, theme }) =>
    $selected
      ? css`
          background: ${theme.white};
          color: ${theme.darkGray};
        `
      : css`
          ${gradientFill}
          color: ${theme.white};
        `}
`;
